import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, FileText, Loader2, CheckCircle2, ChevronLeft, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import driverApi from "@/lib/driverApi";

const eur = (v) => `€${Number(v || 0).toFixed(2)}`;
const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

const STATUS_HR = {
  draft: ["Nacrt", "border-zinc-500 text-zinc-400"],
  approved: ["Odobren", "border-amber-400/50 text-amber-300"],
  fira_synced: ["Fiskaliziran (Fira)", "border-sky-400/50 text-sky-300"],
  paid: ["Isplaćen", "border-emerald-400/50 text-emerald-300"],
};

export default function DriverStatements() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);

  useEffect(() => {
    if (!localStorage.getItem("juke_driver_token")) { navigate("/driver"); return; }
    driverApi.get("/driver/me")
      .then(({ data }) => setMe(data))
      .catch((err) => {
        if (err?.response?.status === 401) navigate("/driver");
        else toast.error("Učitavanje nije uspjelo.");
      });
  }, [navigate]);

  if (!me) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-zinc-500" />
      </div>
    );
  }

  return me.driver_type === "subcontractor"
    ? <SubcontractorStatements navigate={navigate} />
    : <EmployeeEarnings navigate={navigate} />;
}

/* ---------- Internal employee: "Moja Zarada" ---------- */
function EmployeeEarnings({ navigate }) {
  const [month, setMonth] = useState(() => monthKey(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    driverApi.get("/driver/earnings", { params: { month } })
      .then(({ data }) => setData(data))
      .catch((err) => {
        if (err?.response?.status === 401) return navigate("/driver");
        toast.error("Zaradu nije moguće učitati.");
      })
      .finally(() => setLoading(false));
  }, [month, navigate]);

  const shiftMonth = (delta) => {
    const [y, m] = month.split("-").map(Number);
    setMonth(monthKey(new Date(y, m - 1 + delta, 1)));
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="employee-earnings-page">
      <header className="border-b border-white/5 bg-[#0a0a0a]/90 sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center gap-3">
          <button onClick={() => navigate("/driver/dashboard")} data-testid="statements-back"
            className="p-2 rounded-full hover:bg-white/5 text-zinc-400">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Zarada i Vožnje</p>
            <p className="text-sm font-medium" data-testid="earnings-title">Moja Zarada</p>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        <div className="flex items-center gap-2">
          <button onClick={() => shiftMonth(-1)} data-testid="earnings-month-prev"
            className="p-2 rounded-full border border-white/10 hover:border-[#C5A059] text-zinc-300">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm tabular-nums px-2" data-testid="earnings-month-label">{month}</span>
          <button onClick={() => shiftMonth(1)} data-testid="earnings-month-next"
            className="p-2 rounded-full border border-white/10 hover:border-[#C5A059] text-zinc-300">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-zinc-500 text-sm py-8">
            <Loader2 className="w-4 h-4 animate-spin" /> Učitavanje…
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div className="rounded-2xl border border-white/10 bg-[#0d0d0d] p-4">
                <p className="text-[9px] uppercase tracking-[0.2em] text-zinc-500">Ukupni promet</p>
                <p className="text-xl tabular-nums mt-1" data-testid="employee-gross">{eur(data?.gross_total)}</p>
                <p className="text-[10px] text-zinc-600 mt-0.5">{data?.ride_count || 0} vožnji</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-[#0d0d0d] p-4">
                <p className="text-[9px] uppercase tracking-[0.2em] text-zinc-500">Provizija tvrtke (20%)</p>
                <p className="text-xl text-zinc-300 tabular-nums mt-1" data-testid="employee-commission">{eur(data?.commission_amount)}</p>
              </div>
              <div className="rounded-2xl border border-[#C5A059]/40 bg-[#C5A059]/5 p-4">
                <p className="text-[9px] uppercase tracking-[0.2em] text-[#C5A059]">Moja isplata (80%)</p>
                <p className="text-xl font-display text-[#C5A059] tabular-nums mt-1" data-testid="employee-share">{eur(data?.driver_share)}</p>
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 pt-2">
                Vožnje ovog mjeseca
              </p>
              {!(data?.rides || []).length && (
                <p className="text-sm text-zinc-600" data-testid="employee-rides-empty">
                  Nema završenih vožnji u ovom mjesecu.
                </p>
              )}
              {(data?.rides || []).map((r, i) => (
                <div key={r.ref || i} data-testid={`employee-ride-${i}`}
                  className="flex items-center gap-3 rounded-xl border border-white/5 bg-[#0d0d0d] px-3 py-2.5">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-white truncate">{r.pickup_location} → {r.dropoff_location || "—"}</p>
                    <p className="text-[10px] text-zinc-500 mt-0.5">{r.pickup_date} · {r.pickup_time} · {r.ref}</p>
                  </div>
                  <p className="text-sm text-zinc-200 tabular-nums">{eur(r.quote_amount)}</p>
                </div>
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
}

/* ---------- Subcontractor: self-billing statements ---------- */
function SubcontractorStatements({ navigate }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = () =>
    driverApi.get("/driver/self-billing/statements")
      .then(({ data }) => setRows(data || []))
      .catch((err) => {
        if (err?.response?.status === 401) return navigate("/driver");
        toast.error("Obračune nije moguće učitati.");
      })
      .finally(() => setLoading(false));

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openPdf = async (s) => {
    setBusy(s.id + "pdf");
    try {
      const { data } = await driverApi.get(`/driver/self-billing/statements/${s.id}/pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(new Blob([data], { type: "application/pdf" })), "_blank");
    } catch { toast.error("PDF nije dostupan"); }
    finally { setBusy(null); }
  };

  const accept = async (s) => {
    setBusy(s.id + "acc");
    try {
      await driverApi.post(`/driver/self-billing/statements/${s.id}/accept`);
      toast.success("Obračun prihvaćen");
      load();
    } catch { toast.error("Nije uspjelo"); }
    finally { setBusy(null); }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="subcontractor-statements-page">
      <header className="border-b border-white/5 bg-[#0a0a0a]/90 sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center gap-3">
          <button onClick={() => navigate("/driver/dashboard")} data-testid="statements-back"
            className="p-2 rounded-full hover:bg-white/5 text-zinc-400">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Samoizdavanje računa</p>
            <p className="text-sm font-medium">Moji Obračuni</p>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-3">
        {loading && (
          <div className="flex items-center gap-2 text-zinc-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Učitavanje…
          </div>
        )}
        {!loading && !rows.length && (
          <p className="text-sm text-zinc-600" data-testid="statements-empty">
            Nema obračuna. Mjesečni obračuni pojavit će se ovdje nakon generiranja.
          </p>
        )}
        {rows.map((s) => {
          const [label, cls] = STATUS_HR[s.status] || STATUS_HR.draft;
          return (
            <article key={s.id} data-testid={`statement-${s.id}`} className="rounded-2xl border border-white/10 bg-[#0d0d0d] p-4">
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-[200px]">
                  <p className="text-sm text-white">
                    Samoizdani račun {s.number}
                    <span className={`ml-2 text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${cls}`}>{label}</span>
                  </p>
                  <p className="text-xs text-zinc-500 mt-1">
                    {s.period_start} – {s.period_end} · {s.ride_count} vožnji
                  </p>
                  <p className="text-xs text-zinc-400 mt-1.5">
                    Bruto {eur(s.gross_total)} · Provizija 20% −{eur(s.commission_amount)}
                    {s.is_vat_registered ? ` · PDV ${s.vat_rate_pct}% ${eur(s.vat_amount)}` : ""}
                  </p>
                </div>
                <p className="text-xl font-display text-[#C5A059] tabular-nums">{eur(s.net_payout)}</p>
              </div>
              <div className="flex gap-2 mt-3">
                <button data-testid={`statement-pdf-${s.id}`} onClick={() => openPdf(s)} disabled={busy === s.id + "pdf"}
                  className="h-9 px-4 rounded-full border border-white/15 text-zinc-300 hover:border-[#C5A059] text-[10px] uppercase tracking-widest flex items-center gap-1.5">
                  {busy === s.id + "pdf" ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />} PDF
                </button>
                {!s.driver_accepted ? (
                  <button data-testid={`statement-accept-${s.id}`} onClick={() => accept(s)} disabled={busy === s.id + "acc"}
                    className="h-9 px-4 rounded-full bg-[#C5A059] text-black text-[10px] uppercase tracking-widest font-semibold flex items-center gap-1.5">
                    {busy === s.id + "acc" ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                    Prihvati obračun
                  </button>
                ) : (
                  <span className="h-9 px-4 rounded-full border border-emerald-500/40 text-emerald-300 text-[10px] uppercase tracking-widest flex items-center gap-1.5">
                    <CheckCircle2 className="w-3 h-3" /> Prihvaćen
                  </span>
                )}
              </div>
            </article>
          );
        })}
      </main>
    </div>
  );
}
